import React, { FC, MouseEventHandler } from 'react'
import {
  styled,
  breakpoint,
  breakpointDown,
  screenReaderOnly,
} from '@moonpig/launchpad-utils'
import {
  sizingSpacing,
  SizingSpacingSystemProps,
} from '@moonpig/launchpad-system'
import { spacingPx } from '@moonpig/launchpad-theme'
import { useLocaleText } from '../locale'
import { Breadcrumb as BreadcrumbComponent } from './Breadcrumb'

type Breadcrumb = {
  url: string
  label: React.ReactNode
}

type BreadcrumbsProps = {
  items: Breadcrumb[]
  onClick?: (params: {
    url: string
    depth: number
    label: React.ReactNode
  }) => Promise<void>
} & SizingSpacingSystemProps

const BREAKPOINT = 'lg'

const StyledBreadcrumbs = styled.nav<SizingSpacingSystemProps>`
  ${sizingSpacing}
`

const StyledBreadcrumb = styled.li<{
  parentPage: boolean
  currentPage: boolean
}>`
  ${breakpointDown(BREAKPOINT)} {
    ${({ parentPage }) => (parentPage ? '' : screenReaderOnly)};
  }

  ${breakpoint(BREAKPOINT)} {
    display: inline-block;
  }
`
const StyledList = styled.ol`
  display: flex;
  flex-direction: row;
  gap: ${spacingPx(4)};
  align-items: center;
`

export const Breadcrumbs: FC<React.PropsWithChildren<BreadcrumbsProps>> = ({
  items,
  onClick,
  ...props
}) => {
  const t = useLocaleText()

  return (
    <StyledBreadcrumbs aria-label={t('breadcrumbs.breadcrumbs')} {...props}>
      <StyledList role="list">
        {items.map(({ url, label }, index) => {
          const parentPage = items.length === index + 2
          const currentPage = items.length === index + 1
          const showIntermediateLink = index < items.length - 1

          const onClickHandler: MouseEventHandler<HTMLElement> = async evt => {
            if (onClick) {
              evt.preventDefault()
              await onClick({ url, depth: index, label })
              window.location.href = url
            }
          }

          return (
            <StyledBreadcrumb
              key={url}
              parentPage={parentPage}
              currentPage={currentPage}
            >
              <BreadcrumbComponent
                url={url}
                label={label}
                showIntermediateLink={showIntermediateLink}
                parentPage={parentPage}
                currentPage={currentPage}
                onClickHandler={onClickHandler}
              />
            </StyledBreadcrumb>
          )
        })}
      </StyledList>
    </StyledBreadcrumbs>
  )
}
